<script setup lang="ts">
  import type { DropdownProps } from 'naive-ui'

  import { type AvailableLocale, LOCALE_ITEMS, useUplatformLocale } from '@/features/locale'

  import LocaleOption from './LocaleOption.vue'

  interface Props {
    direction?: 'up' | 'down'
    variant?: 'default' | 'auth'
  }

  const props = withDefaults(defineProps<Props>(), {
    direction: 'down',
    variant: 'default',
  })

  const dropdownOptionRowClass =
    'mx-1 flex h-8 cursor-pointer items-center rounded-lg px-2 text-(--color-body) hover:bg-(--color-secondary-bg)'

  const { t } = useI18n()
  const { setLocale, currentLocale } = useUplatformLocale()

  const menuIsActive = ref(false)

  const selectedLocale = computed(() => LOCALE_ITEMS.find((locale) => locale.value === currentLocale.value))

  const triggerAriaLabel = computed(() =>
    selectedLocale.value
      ? t('base_aria_language_switcher', { language: selectedLocale.value.alt })
      : t('header_dropdown-language'),
  )

  const dropdownPlacement = computed(() => (props.direction === 'up' ? 'top' : 'bottom'))

  const handleSelect = (key: AvailableLocale) => {
    setLocale(key)
    menuIsActive.value = false
  }

  const options = computed<NonNullable<DropdownProps['options']>>(() =>
    LOCALE_ITEMS.map((locale) => ({
      type: 'render',
      key: locale.value,
      props: {
        'data-dropdown-option': true,
        role: 'menuitemradio',
        'aria-checked': `${locale.value === currentLocale.value}`,
        'aria-label': locale.alt,
        class: dropdownOptionRowClass,
        onClick: () => handleSelect(locale.value),
      },
      render: () => h(LocaleOption, { icon: locale.icon, name: locale.name }),
    })),
  )
</script>

<template>
  <NDropdown
    v-model:show="menuIsActive"
    :options="options"
    :placement="dropdownPlacement"
    trigger="click"
    :show-arrow="false"
    size="medium"
  >
    <button
      class="flex min-w-[92px] cursor-pointer items-center gap-2 border-none bg-transparent p-0"
      type="button"
      aria-haspopup="menu"
      :aria-expanded="menuIsActive"
      :aria-label="triggerAriaLabel"
    >
      <template v-if="selectedLocale">
        <span
          class="min-w-11 text-right text-sm leading-6 font-semibold text-(--color-body)"
          aria-hidden="true"
        >
          {{ selectedLocale.name }}
        </span>
        <img
          :src="selectedLocale.icon"
          alt=""
          class="size-4 shrink-0"
          aria-hidden="true"
        />
      </template>
      <svg
        class="size-3 shrink-0 text-(--color-body) transition-transform duration-200"
        :class="{ 'rotate-180deg': menuIsActive }"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M2 4L6 8L10 4"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </NDropdown>
</template>
